import type { CurrentUser, ReceiveMode, RoomFilter } from "@catmail/shared";

import { Avatar, Badge } from "@/components";
import { cn } from "@/lib/cn";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";

import { SelfProfilePopover, type PushControl } from "../SelfProfilePopover";

/**
 * 좁은 화면에서 다크 레일을 대신하는 줄.
 *
 * 레일은 176px을 세로로 통째로 쓴다. 화면이 360px인 곳에서 그걸 그대로 두면
 * 목록에 남는 폭이 없다. 그래서 레일을 숨기는데, **숨기기만 하면 필터·프로필·
 * 로그아웃이 전부 닿을 수 없는 곳으로 사라진다.** 여기가 그 자리다.
 *
 * **목록 맨 아래**에 붙는다. 위에 두면 제목·검색과 겹쳐 머리가 세 겹이 되고,
 * 한 손으로 들었을 때 제일 자주 누르는 것이 엄지에서 제일 먼 곳에 놓인다.
 *
 * 레일을 가로로 눕힌 것이 아니라 다시 만든 것은, 세로로 쌓인 어두운 판과
 * 가로로 흐르는 밝은 줄이 같은 디자인이 아니기 때문이다
 * (react-component-rules §1은 **같은 생김새**일 때 같은 컴포넌트를 쓰라고 한다).
 */

const TABS: ReadonlyArray<{ filter: RoomFilter; label: string }> = [
  { filter: "all", label: "전체" },
  { filter: "direct", label: "1:1 대화" },
  { filter: "group", label: "여러 명" },
  { filter: "quiet", label: "조용한 방" },
];

type MobileNavProps = {
  user: CurrentUser;
  filter: RoomFilter;
  counts: Record<RoomFilter, number>;
  onFilterChange: (filter: RoomFilter) => void;
  onSignOut?: () => void;
  onChangeReceiveMode?: (mode: ReceiveMode) => void;
  onRename?: () => void;
  onChangeAvatar?: () => void;
  onChangePassword?: () => void;
  onAddAddress?: () => void;
  /** 주소 하나를 손보는 창. 팝오버가 그대로 넘겨받는다 */
  onOpenAddress?: (identityId: string) => void;
  /** 지금 쓰는 주소 = 지금 계정 */
  currentIdentityId?: string | null;
  /** 주소별 안 읽음 */
  unreadByIdentity?: Record<string, number>;
  onSelectIdentity?: (identityId: string) => void;
  /** 최고 관리자에게만 */
  onOpenAdmin?: (() => void) | undefined;
  /** 이 기기 알림. 팝오버가 그대로 넘겨받는다 */
  push?: PushControl;
  className?: string;
};

export function MobileNav({
  user,
  filter,
  counts,
  onFilterChange,
  onSignOut,
  onChangeReceiveMode,
  onRename,
  onChangeAvatar,
  onChangePassword,
  onAddAddress,
  onOpenAddress,
  currentIdentityId,
  unreadByIdentity,
  onSelectIdentity,
  onOpenAdmin,
  push,
  className,
}: MobileNavProps) {
  return (
    <div
      className={cn(
        // 목록 맨 아래에 붙는다. 선은 목록 쪽인 위로 긋는다.
        "border-line-soft flex shrink-0 items-center gap-2 border-t px-3 py-2.5",
        className,
      )}
    >
      <SelfProfilePopover
        user={user}
        onSignOut={onSignOut}
        onChangeReceiveMode={onChangeReceiveMode}
        onRename={onRename}
        onChangeAvatar={onChangeAvatar}
        onChangePassword={onChangePassword}
        onAddAddress={onAddAddress}
        onOpenAddress={onOpenAddress}
        currentIdentityId={currentIdentityId}
        unreadByIdentity={unreadByIdentity}
        onSelectIdentity={onSelectIdentity}
        onOpenAdmin={onOpenAdmin}
        push={push}
      >
        {/*
          이름을 같이 적는다.

          넓은 화면의 레일은 맨 아래에 아바타·이름·주소를 한 줄로 세워둬서 그
          자리가 "나"라는 것이 그냥 보인다. 여기는 오랫동안 이름표 없는 동그라미
          하나였고, 그래서 **폰에서는 이름·사진·비밀번호를 바꿀 길이 없다고**
          알고 쓰는 사람이 있었다. 있는데 못 찾는 것은 없는 것과 같다.
        */}
        <button
          type="button"
          aria-label="내 프로필"
          className={`bg-surface-3 hover:bg-chip active:bg-line-soft flex max-w-32 shrink-0 items-center gap-1.5 rounded-full py-1 pr-2.5 pl-1 ${PRESS_FLAT} ${FOCUS}`}
        >
          <Avatar
            size="sm"
            shape="circle"
            initials={user.initials}
            colorIndex={user.colorIndex}
            imageUrl={user.avatarUrl}
            label={user.displayName}
          />
          <span className="text-body truncate text-[13px] font-medium">
            {user.displayName}
          </span>
        </button>
      </SelfProfilePopover>

      {/*
        탭이 네 개라 좁은 화면에서는 한 줄에 안 들어간다. 줄바꿈하면 목록이
        아래로 밀리므로 옆으로 흘려보낸다. 스크롤바는 배경에 녹아 있다.
      */}
      <div
        role="tablist"
        aria-label="채팅방 분류"
        className="-mx-1 flex flex-1 gap-1 overflow-x-auto px-1"
      >
        {TABS.map((tab) => {
          const active = filter === tab.filter;

          return (
            <button
              key={tab.filter}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onFilterChange(tab.filter)}
              className={cn(
                `flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] whitespace-nowrap ${PRESS_FLAT} ${FOCUS}`,
                active
                  ? "bg-ink text-paper font-semibold"
                  : "bg-surface-3 text-body hover:bg-chip active:bg-line-soft",
              )}
            >
              {tab.label}
              <Badge
                count={counts[tab.filter]}
                size="sm"
                tone={active ? "onDark" : "alert"}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
